import { Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";
import { lazyWithRecovery } from "./lib/chunk-recovery";

// Heavier / less-frequent routes are lazy-loaded so they don't bloat the initial bundle.
const Booking = lazyWithRecovery(() => import("./pages/Booking"));
const Admin = lazyWithRecovery(() => import("./pages/Admin"));
const PrivacyPolicy = lazyWithRecovery(() => import("./pages/PrivacyPolicy"));
const TermsOfUse = lazyWithRecovery(() => import("./pages/TermsOfUse"));
const Copyright = lazyWithRecovery(() => import("./pages/Copyright"));
const ResetPassword = lazyWithRecovery(() => import("./pages/ResetPassword"));
const Unsubscribe = lazyWithRecovery(() => import("./pages/Unsubscribe"));
const MyProfile = lazyWithRecovery(() => import("./pages/MyProfile"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/booking" element={<Booking />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/privacidade" element={<PrivacyPolicy />} />
              <Route path="/termos" element={<TermsOfUse />} />
              <Route path="/direitos-autorais" element={<Copyright />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/meus-dados" element={<MyProfile />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
